import chroma from 'chroma-js';

export type FusePaletteShade =
    | '50'
    | '100'
    | '200'
    | '300'
    | '400'
    | '500'
    | '600'
    | '700'
    | '800'
    | '900';

export type FusePrimaryPalette = Record<FusePaletteShade, string> & {
    DEFAULT: string;
    contrast: string;
};

const SHADES: FusePaletteShade[] = [
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
];

const luminance = (color: string): number => {
    const [r, g, b] = chroma(color).rgb().map((v) => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getContrastColor = (bgColor: string): string =>
    luminance(bgColor) > 0.5 ? '#000000' : '#ffffff';

/**
 * Generates a Fuse-compatible primary palette from a single hex color.
 */
export function generatePalette(baseColor: string): FusePrimaryPalette | null {
    if (!chroma.valid(baseColor)) {
        return null;
    }

    const palette: Record<FusePaletteShade, string | null> = {
        50: null,
        100: null,
        200: null,
        300: null,
        400: null,
        500: baseColor,
        600: null,
        700: null,
        800: null,
        900: null,
    };

    const colors = Object.values(palette).filter(Boolean) as string[];

    colors.unshift(
        chroma.scale(['white', baseColor]).mode('lrgb').colors(50)[1]
    );

    colors.push(
        chroma.scale(['black', baseColor]).mode('lrgb').colors(10)[1]
    );

    const domain = [
        0,
        ...SHADES.filter((shade) => palette[shade]).map(
            (shade) => parseInt(shade, 10) / 1000
        ),
        1,
    ];

    const scale = chroma.scale(colors).domain(domain).mode('lrgb');

    const finalPalette = Object.fromEntries(
        SHADES.map((shade) => [shade, scale(parseInt(shade, 10) / 1000).hex()])
    ) as Record<FusePaletteShade, string>;

    const defaultShade = finalPalette['600'];

    return {
        ...finalPalette,
        DEFAULT: defaultShade,
        contrast: getContrastColor(defaultShade),
    };
}

export function generateContrasts(
    palette: Record<string, string>
): Record<string, string> {
    const lightColor = '#FFFFFF';
    let darkColor = '#FFFFFF';

    Object.values(palette).forEach((color) => {
        if (!chroma.valid(color)) {
            return;
        }

        darkColor =
            chroma.contrast(color, '#FFFFFF') > chroma.contrast(darkColor, '#FFFFFF')
                ? color
                : darkColor;
    });

    return Object.fromEntries(
        Object.entries(palette).map(([hue, color]) => [
            hue,
            chroma.contrast(color, darkColor) > chroma.contrast(color, lightColor)
                ? darkColor
                : lightColor,
        ])
    );
}
